/**
 * Map or create the Deeliver category for an unmapped Vend category.
 *
 * Processing Rules → Category → "When the category isn't mapped: Stage" holds every new
 * item in a Vend category nobody has mapped yet. This dialog is the decision: point the
 * Vend category at an existing Deeliver category, or create a new one (prefilled with
 * the Vend name). Either way the held items become approvable at once — the category is
 * resolved at approval, so no re-sync is needed. Used from the staging queue and from
 * Menu Mappings.
 */
import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';
import authInstance from '../services/httpClient';
import { stagingApiBase } from '../pages/vaperite/stagingOrg';
import { appCategoryOptions } from '../pages/vaperite/appCategories';

interface LocalCategory {
  id: string;
  name: string;
  isActive: boolean;
  homeFilterType: string | null;
}

interface Props {
  /** The Vend category to resolve; null keeps the dialog closed. */
  vendCategory: string | null;
  /** Items waiting on it, when known — shown so the size of the decision is visible. */
  waitingCount?: number;
  /** Which choice is selected on open: the queue opens on Map, Menu Mappings' create button on Create. */
  initialMode?: 'map' | 'create';
  onClose: () => void;
  /** Called after the mapping exists. */
  onResolved: () => void;
}

export default function ResolveUnmappedCategoryDialog({ vendCategory, waitingCount, initialMode = 'map', onClose, onResolved }: Props) {
  const notify = useNotify();
  const [mode, setMode] = useState<'map' | 'create'>('map');
  const [categories, setCategories] = useState<LocalCategory[]>([]);
  const [categoryId, setCategoryId] = useState('');
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!vendCategory) return;
    setMode(initialMode);
    setCategoryId('');
    setNewName(vendCategory);
    authInstance
      .get(`${stagingApiBase()}/staging/category-mappings/local`)
      .then((res) => setCategories(appCategoryOptions<LocalCategory>(res.data?.categories ?? [])))
      .catch(() => notify('Failed to load Deeliver App Categories', { type: 'error' }));
  }, [vendCategory, initialMode, notify]);

  const canSave = mode === 'map' ? Boolean(categoryId) : newName.trim().length > 0;

  const save = async () => {
    if (!vendCategory || !canSave) return;
    setSaving(true);
    try {
      if (mode === 'map') {
        await authInstance.post(`${stagingApiBase()}/staging/category-mappings`, {
          payloadCategoryName: vendCategory,
          localCategoryId: categoryId,
        });
        const name = categories.find((c) => c.id === categoryId)?.name;
        notify(`"${vendCategory}" now maps to ${name}`, { type: 'success' });
      } else {
        const res = await authInstance.post(`${stagingApiBase()}/staging/category-mappings/create-category`, {
          payloadCategoryName: vendCategory,
          categoryName: newName.trim(),
        });
        const { created, categoryName } = res.data ?? {};
        notify(
          created
            ? `Created ${categoryName} and mapped "${vendCategory}" to it`
            : `${categoryName} already existed — mapped "${vendCategory}" to it`,
          { type: 'success' },
        );
      }
      onResolved();
      onClose();
    } catch (err: any) {
      notify(`Failed: ${err.response?.data?.error || err.message}`, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(vendCategory)} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Unmapped category: {vendCategory}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {waitingCount
            ? `${waitingCount} new item${waitingCount === 1 ? ' is' : 's are'} waiting on this Vend category.`
            : 'New items in this Vend category are held until it is mapped.'}
        </Typography>
        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as 'map' | 'create')}>
          <FormControlLabel value="map" control={<Radio size="small" />} label="Map to an existing category" />
          <FormControlLabel value="create" control={<Radio size="small" />} label="Create a new category" />
        </RadioGroup>
        <Stack sx={{ mt: 2 }}>
          {mode === 'map' ? (
            <FormControl size="small" fullWidth>
              <InputLabel>Deeliver App Category</InputLabel>
              <Select label="Deeliver App Category" value={categoryId} onChange={(e) => setCategoryId(String(e.target.value))}>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              size="small"
              label="New category name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              helperText="Created active and empty; it shows in the shop once approved products land in it. Set its image and position under Categories."
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={!canSave || saving}>
          {mode === 'map' ? 'Map' : 'Create & map'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
